import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../common/LoadingSpinner';
import mosqueService from '../../services/mosqueService';
import { getMosqueUrl } from '../../utils/urlHelpers';
import { getListingCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

function parseListResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

function societyDisplay(mosque) {
  if (mosque?.society_name) return mosque.society_name;
  const s = mosque?.society;
  if (s && typeof s === 'object') return s.name || '';
  return '';
}

/**
 * Paginated mosque directory grid (shared by /mosques and /islam/mosques).
 * variant: 'default' light page styling; 'islam' matches IslamFuturisticShell glass/dark theme.
 */
const MosquesDirectoryList = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 24;
  const islam = variant === 'islam';

  const loadPage = useCallback(async (nextPage, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await mosqueService.getMosques({
        page: nextPage,
        page_size: pageSize,
      });
      const rows = parseListResponse(res);
      if (append) {
        setMosques((prev) => [...prev, ...rows]);
      } else {
        setMosques(rows);
      }
      setHasMore(Boolean(res?.next));
      setPage(nextPage);
    } catch (err) {
      console.error(err);
      setError('Failed to load mosques. Please try again.');
      if (!append) setMosques([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const cardClass = islam
    ? 'text-left rounded-xl border border-white/10 bg-slate-900/55 shadow-lg shadow-black/20 overflow-hidden hover:border-cyan-400/35 hover:bg-slate-900/75 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950'
    : 'text-left bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2';

  const thumbWrapClass = islam
    ? 'h-36 bg-slate-800/80 flex items-center justify-center overflow-hidden'
    : 'h-36 bg-gray-100 flex items-center justify-center overflow-hidden';

  const titleClass = islam ? 'font-semibold text-white line-clamp-2' : 'font-semibold text-gray-900 line-clamp-2';
  const subClass = islam ? 'text-sm text-slate-400 mt-1 line-clamp-1' : 'text-sm text-gray-500 mt-1 line-clamp-1';
  const addrClass = islam ? 'text-sm text-slate-300 mt-2 line-clamp-2' : 'text-sm text-gray-600 mt-2 line-clamp-2';
  const emptyClass = islam ? 'col-span-full text-center text-slate-400 py-12' : 'col-span-full text-center text-gray-500 py-12';
  const errClass = islam
    ? 'mb-6 rounded-lg border border-red-400/30 bg-red-950/40 px-4 py-3 text-sm text-red-200'
    : 'mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800';
  const loadMoreClass = islam
    ? 'rounded-xl border border-cyan-400/40 bg-gradient-to-r from-cyan-500/20 to-violet-600/25 px-6 py-2.5 text-sm font-semibold text-white hover:border-cyan-300/60 disabled:opacity-50'
    : 'rounded-lg bg-blue-600 text-white px-6 py-2.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50';

  return (
    <div className={islam ? 'text-left' : ''}>
      {error && <div className={errClass}>{error}</div>}

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mosques.length === 0 ? (
              <p className={emptyClass}>No mosques listed yet.</p>
            ) : (
              mosques.map((m) => {
                const coverUrl = getListingCoverImageUrl(m);
                const hasCover = coverUrl !== IMAGE_PLACEHOLDER_PATH;
                const sub = societyDisplay(m);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => navigate(getMosqueUrl(m))}
                    className={cardClass}
                  >
                    <div className={thumbWrapClass}>
                      {hasCover ? (
                        <img
                          src={coverUrl}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                          }}
                        />
                      ) : (
                        <span className="text-4xl" aria-hidden>
                          🕌
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h2 className={titleClass}>{m.name}</h2>
                      {sub && <p className={subClass}>{sub}</p>}
                      {m.address && <p className={addrClass}>{m.address}</p>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {hasMore && mosques.length > 0 && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => loadPage(page + 1, true)}
                disabled={loadingMore}
                className={loadMoreClass}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MosquesDirectoryList;
